import { useState, useRef, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  InputBase,
  Divider,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import { useGetOne, useGetList, RaRecord } from "react-admin";
import { ObjectType } from "@/types";
import { resourceIcons } from "@/utils/icons";
import { RECORD_TYPES, getRecordIdentity, getRecordTypeConfig } from "../../utils/taskUtils";

interface RecordSelectorProps {
  /** Currently selected object type */
  objectType: ObjectType | null;
  /** Currently selected object ID */
  objectId: string | null;
  /** Display label (used in read-only mode if provided) */
  label?: string;
  /** Whether the selector is editable */
  editable?: boolean;
  /** Callback when selection changes (required if editable) */
  onChange?: (objectType: ObjectType | null, objectId: string | null, label?: string) => void;
  /** Placeholder text when no record selected */
  placeholder?: string;
  /** Hide completely if no record selected (useful for read-only displays) */
  hideIfEmpty?: boolean;
  /** Read-only link target; defaults to the record's show page (e.g. a task deep link). */
  to?: string;
  /** Extra list filter that scopes the record search (e.g. `{ client_id_ref }`). */
  filter?: Record<string, string>;
  /** Hide "Change type" so only records of `objectType` can be picked. */
  lockType?: boolean;
}

// Keys MUI's MenuList handles for navigation/close — these must keep bubbling
// from the search input so arrow-nav into the list and Escape-to-close still
// work. Everything else (printable keys) is stopped to disable type-to-focus.
const MENU_NAV_KEYS = ["ArrowDown", "ArrowUp", "Escape", "Enter", "Tab", "Home", "End"];

export function RecordSelector({
  objectType,
  objectId,
  label,
  editable = false,
  onChange,
  placeholder = "Select record...",
  hideIfEmpty = false,
  to,
  filter,
  lockType = false,
}: RecordSelectorProps) {
  const anchorRef = useRef<HTMLElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuMode, setMenuMode] = useState<"type" | "record">("type");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce the search query before hitting the server (mirrors useRefCodeSearch).
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Resolve the API resource path from the record-type config — NOT from
  // objectType. ObjectType.REIMBURSEMENTS is the singular "reimbursement", but
  // the endpoint is the plural "reimbursements" (ResourceName.REIMBURSEMENTS);
  // deriving the path from objectType silently fetched the wrong (empty) URL.
  const recordTypeConfig = getRecordTypeConfig(objectType);
  const resource = recordTypeConfig?.resource ?? objectType?.replace(/_/g, "-") ?? "";
  const RecordIcon = recordTypeConfig ? resourceIcons[recordTypeConfig.resource] : null;

  // Server-side search: push the query to the backend (q=) rather than fetching a
  // capped page and filtering client-side, which silently truncated large lists
  // (the old perPage:100 made records 101+ unsearchable). Only fetches while the
  // record-selection menu is open.
  const trimmedQuery = debouncedQuery.trim();
  const { data: records = [], isFetching: isFetchingRecords } = useGetList(
    resource,
    {
      filter: { ...filter, ...(trimmedQuery ? { q: trimmedQuery } : {}) },
      pagination: { page: 1, perPage: 25 },
    },
    { enabled: editable && !!objectType && menuMode === "record" }
  );

  // Fetch the selected record details
  const { data: selectedRecord } = useGetOne(resource, { id: objectId || "" }, { enabled: !!objectType && !!objectId });

  // Derived display values — mirror the resource's Show-page header (primary +
  // optional secondary line). Falls back to the stored `label` until the record
  // resolves.
  const selectedIdentity = selectedRecord ? getRecordIdentity(objectType, selectedRecord) : null;
  const displayLabel = selectedIdentity?.primary || label || null;
  const displaySecondary = selectedIdentity?.secondary;
  const displayTypeName = recordTypeConfig?.name ?? null;

  // Build the link path for read-only mode
  const recordPath = to ?? (resource && objectId ? `/${resource}/${objectId}/show` : null);

  // Early return if empty and hideIfEmpty is set
  if (hideIfEmpty && !objectId) {
    return <Box />;
  }

  // Reset both the raw and debounced query. Clearing only searchQuery leaves the
  // debounced value stale for ~300ms, so a context switch (Back / change type)
  // would re-enter record mode and fire the next list fetch with the old q=.
  const resetSearch = () => {
    setSearchQuery("");
    setDebouncedQuery("");
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!editable) return;
    anchorRef.current = e.currentTarget;
    setMenuMode(objectType ? "record" : "type");
    resetSearch();
    setMenuOpen(true);
  };

  const handleTypeSelect = (type: ObjectType) => {
    onChange?.(type, null, undefined);
    setMenuMode("record");
    resetSearch();
  };

  const handleRecordSelect = (record: RaRecord) => {
    // Persist the flattened identity so the stored task-reference label stays
    // human-readable even before the record re-resolves.
    const identity = getRecordIdentity(objectType, record);
    const labelStr = [identity.primary, identity.secondary].filter(Boolean).join(" — ");
    onChange?.(objectType, record.id as string, labelStr);
    setMenuOpen(false);
    resetSearch();
  };

  const handleBackToTypes = () => {
    setMenuMode("type");
    resetSearch();
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
    resetSearch();
  };

  // Shared content for the display
  const renderContent = () => {
    if (!objectId || !displayLabel) {
      return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography color="text.secondary" sx={{ fontSize: 14 }}>
            {placeholder}
          </Typography>
          {editable && <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "text.disabled" }} />}
        </Stack>
      );
    }

    return (
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {RecordIcon && <RecordIcon sx={{ fontSize: 20, color: "primary.main" }} />}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, display: "block" }}>
            {displayTypeName}
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {displayLabel}
          </Typography>
          {displaySecondary && (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: "block" }}>
              {displaySecondary}
            </Typography>
          )}
        </Box>
      </Stack>
    );
  };

  // Box styles
  const baseBoxStyles = {
    px: 1.5,
    py: 0.75,
    borderRadius: 1.5,
    transition: "all 0.15s",
  };

  const emptyEditableStyles = {
    ...baseBoxStyles,
    border: "1px dashed",
    borderColor: "divider",
    cursor: "pointer",
    "&:hover": {
      borderColor: "text.secondary",
      bgcolor: "action.hover",
    },
  };

  const filledStyles = {
    ...baseBoxStyles,
    "&:hover": { bgcolor: "action.hover" },
  };

  // Read-only mode: render as link
  if (!editable) {
    if (!recordPath) {
      return <Box sx={filledStyles}>{renderContent()}</Box>;
    }

    return (
      <Box
        component={Link}
        to={recordPath}
        sx={{
          ...filledStyles,
          textDecoration: "none",
          color: "inherit",
          cursor: "pointer",
        }}
      >
        {renderContent()}
      </Box>
    );
  }

  // Editable mode: render as selector with menu
  const isEmptyState = !objectId || !displayLabel;

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          ...(isEmptyState ? emptyEditableStyles : filledStyles),
          cursor: "pointer",
        }}
      >
        {renderContent()}
      </Box>

      <Menu
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={handleMenuClose}
        // No body scroll-lock: the inline task composer opens this, and a lock
        // leaked by a menu unmounting while open froze the drawer (#5158 / #6310).
        disableScrollLock
        slotProps={{
          paper: {
            sx: { borderRadius: 2, minWidth: 280, maxHeight: 400 },
          },
        }}
      >
        {menuMode === "type" ? (
          // Type selection
          RECORD_TYPES.map((t) => {
            const Icon = resourceIcons[t.resource];
            return (
              <MenuItem key={t.id} onClick={() => handleTypeSelect(t.id)}>
                <ListItemIcon>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t.name}</ListItemText>
              </MenuItem>
            );
          })
        ) : (
          // Record selection with search
          <>
            {!lockType && (
              <MenuItem onClick={handleBackToTypes} sx={{ color: "text.secondary" }}>
                <ListItemIcon>
                  <ArrowBackIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change type</ListItemText>
              </MenuItem>
            )}

            {!lockType && <Divider />}

            {/* Search input */}
            <Box sx={{ px: 1.5, py: 1 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              >
                <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                <InputBase
                  placeholder={`Search ${recordTypeConfig?.name?.toLowerCase() || "record"}s...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  // Stop only printable keys from bubbling to MUI's MenuList,
                  // which otherwise hijacks them for type-to-focus (e.g. typing
                  // "c" jumps focus to "Change type"). Navigation/close keys keep
                  // bubbling so arrow-nav into the list and Escape still work.
                  onKeyDown={(e) => {
                    if (!MENU_NAV_KEYS.includes(e.key)) e.stopPropagation();
                  }}
                  autoFocus
                  fullWidth
                  sx={{ fontSize: 14 }}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Loading state — use isFetching so the spinner shows on every
                debounced refetch, not just the initial load */}
            {isFetchingRecords && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            {/* Empty state — only while not fetching, so it doesn't flash
                "No matches" mid-search */}
            {!isFetchingRecords && records.length === 0 && (
              <MenuItem disabled>
                <ListItemText>{searchQuery ? "No matches found" : "No records found"}</ListItemText>
              </MenuItem>
            )}

            {/* Record list */}
            {!isFetchingRecords && (
              <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
                {records.map((r: RaRecord) => {
                  const isSelected = objectId === r.id;
                  const identity = getRecordIdentity(objectType, r);
                  return (
                    <MenuItem key={r.id} selected={isSelected} onClick={() => handleRecordSelect(r)}>
                      {isSelected && (
                        <ListItemIcon>
                          <CheckIcon fontSize="small" />
                        </ListItemIcon>
                      )}
                      <ListItemText
                        inset={!isSelected}
                        primary={identity.primary}
                        secondary={identity.secondary}
                        slotProps={{
                          primary: { noWrap: true },
                          secondary: { noWrap: true, sx: { fontSize: 12 } },
                        }}
                      />
                    </MenuItem>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Menu>
    </>
  );
}
